import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as _ from 'lodash';
import { ReportWrapper } from './styles';
import { ColDef, ExcelStyle, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent } from 'ag-grid-community';
import { ICenterBillingRecord, IInvoiceCreditPaymentsReport } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { OrganType } from '@nkr/enums/organ-type';
import * as moment from "moment";
import { Label } from 'react-bootstrap';

interface IProps {

    dataPayment: IInvoiceCreditPaymentsReport[],
    dataCCBReceivable: ICenterBillingRecord[],
    dataOpoReceivable: ICenterBillingRecord[],
    centerId: number,
    organType: OrganType,
    onViewClicked: (invoiceNumber: number) => void,
    onGridReady: (e: GridReadyEvent) => void,
    onReceivableGridReady: (e: GridReadyEvent) => void
}

const formatCurrency = (value: number) => {
    if (value == null) return '';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 32;
const PINNED_HEIGHT = 34;
const BORDER_PAD = 18;
const MAX_ROWS = 8;

const Grid = (props: IProps) => {
    const isNkr = props.organType == OrganType.Kidney;

    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
        setTimeout(() => e.api.sizeColumnsToFit(), 0);
    };

    const onReceivableGridReady = (e: GridReadyEvent) => {
        props.onReceivableGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        setTimeout(() => e.api.sizeColumnsToFit(), 0);
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        setTimeout(() => e.api.sizeColumnsToFit(), 0);
    };
    const handleView = (invoiceNumber: number) => props.onViewClicked(invoiceNumber);

    const getPaymentTotals = (data: IInvoiceCreditPaymentsReport[]) => {
        if (!data || data.length === 0) return [];
        return [{ invoiceNumber: 'Total Invoices Payable', amountRemaining: _.sumBy(data, x => x.amountRemaining ?? 0) }];
    };

    const getReceivableTotals = (data: ICenterBillingRecord[]) => {
        if (!data || data.length === 0) return [];
        return [{ label: 'Total CCB Accounts Receivable', totalOutstanding: _.sumBy(data, x => x.amount ?? 0) }];
    };

    const todayLabel = useMemo(() => moment().format('MM/DD/YYYY'), []);

    const centerName = useMemo(() =>
        props.dataPayment && props.dataPayment.length > 0
            ? props.dataPayment[0]?.centerAlias
            : 'the center'
        , [props.dataCCBReceivable, props.dataOpoReceivable, props.dataPayment]);

    const paymentColumns: ColDef[] = useMemo(() => [
        {
            colId: 'organType',
            field: 'organType',
            headerName: 'Organ Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
            width: 150,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return <span><></></span>;
                let organType = props.organType == null ? '--' : props.organType == 1 ? 'L' : 'K';
                return <Label bsStyle={props.organType == null ? 'default' : props.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
            }
        },
        {
            colId: 'invoiceNumber',
            field: 'invoiceNumber',
            headerName: 'Invoices Payable (Owed to NKR)',
            headerComponentFramework: (p: IHeaderParams) => (
                <SortableHeaderComponent {...p}>Invoices Payable (Owed to NKR)</SortableHeaderComponent>
            ),
            flex: 2,
            minWidth: 220,
            cellRendererFramework: (p: ICellRendererParams) => {
                if (!p.value || p.node.rowPinned) return <span>{p.value}</span>;
                return (
                    <button type="button" className="btn btn-link p-0" onClick={() => handleView(p.value)}>
                        {p.value}
                    </button>
                );
            }
        },
        {
            colId: 'amountRemaining',
            field: 'amountRemaining',
            headerName: `Balance Due (as of ${todayLabel})`,
            headerComponentFramework: (p: IHeaderParams) => (
                <SortableHeaderComponent {...p}>Balance Due (as of {todayLabel})</SortableHeaderComponent>
            ),
            flex: 1,
            minWidth: 180,
            cellClass: ['list-cell-center', 'currency'],
            cellRenderer: (p: ICellRendererParams) => formatCurrency(p.value)
        }
    ], [todayLabel]);

    const receivableColumns: ColDef[] = useMemo(() => [
        {
            colId: 'label',
            field: 'label',
            headerName: `Accounts Receivable (Owed to ${centerName})`,
            headerComponentFramework: (p: IHeaderParams) => (
                <SortableHeaderComponent {...p}>Accounts Receivable (Owed to {centerName})</SortableHeaderComponent>
            ),
            flex: 2,
            minWidth: 220,
        },
        {
            colId: 'totalOutstanding',
            field: 'totalOutstanding',
            headerName: 'Total Outstanding',
            headerComponentFramework: (p: IHeaderParams) => (
                <SortableHeaderComponent {...p}>Total Outstanding</SortableHeaderComponent>
            ),
            flex: 1,
            minWidth: 150,
            cellClass: ['list-cell-center', 'currency'],
            cellRenderer: (p: ICellRendererParams) => formatCurrency(p.value)
        }
    ], [centerName]);


    const paymentRowData = useMemo(() => props.dataPayment ?? [], [props.dataPayment]);
    const receivableRowData = useMemo(() => {
        const rows: { label: string; totalOutstanding: number }[] = [];

        if (props.dataCCBReceivable && props.dataCCBReceivable.length > 0) {
            rows.push({
                label: 'CCB Accounts Receivable',
                totalOutstanding: _.sumBy(props.dataCCBReceivable, x => x.amount ?? 0)
            });
        }

        if (props.dataOpoReceivable && props.dataOpoReceivable.length > 0) {
            rows.push({
                label: 'OPO Accounts Receivable',
                totalOutstanding: _.sumBy(props.dataOpoReceivable, x => x.amount ?? 0)
            });
        }

        return rows;
    }, [props.dataCCBReceivable, props.dataOpoReceivable]);
    const paymentTotals = useMemo(() => getPaymentTotals(props.dataPayment), [props.dataPayment]);
    const receivableTotals = useMemo(() => {
        if (receivableRowData.length == 0) return [];
        return [{
            label: 'Total Accounts Receivable',
            totalOutstanding: _.sumBy(receivableRowData, x => x.totalOutstanding)
        }];
    }, [receivableRowData]);

    // height = header + capped data rows + pinned bottom row + border padding
    const calcHeight = (rowCount: number) =>
        HEADER_HEIGHT + (Math.min(rowCount, MAX_ROWS) * ROW_HEIGHT) + PINNED_HEIGHT + BORDER_PAD;

    const paymentGridHeight = calcHeight(paymentRowData.length);
    const receivableGridHeight = calcHeight(receivableRowData.length);

    const sharedGridProps = {
        defaultColDef: {
            headerClass: 'list-header',
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false,
        },
        headerHeight: 30,
        groupHeaderHeight: 30,
        rowHeight: 30,
        suppressRowClickSelection: false,
        suppressCellSelection: true,
        suppressVerticalScroll: true,   // add this
        onFirstDataRendered,
        onRowDataChanged,
        onSortChanged: (e: SortChangedEvent) => e.api.refreshCells(),
        getRowStyle: (p: any) => {
            if (p.node.rowPinned) {
                return { backgroundColor: '#d7edf8', fontWeight: 700 } as any;
            }
        },
        noRowsOverlayComponentFramework: () => <div>No records found...</div>,
        excelStyles: [
            {
                id: 'currency',
                dataType: 'Number',
                // ag-grid writes formatCode into xml unescaped, so the mask cannot contain double quotes
                numberFormat: { format: '_(\\$* #,##0.00_);_(\\$* \\(#,##0.00\\);_(\\$* \\-??_);_(@_)' }
            }
        ] as ExcelStyle[]
    };

    const wellStyle: React.CSSProperties = {
        width: '50%',
        minWidth: 480,
        padding: '8px',
        marginBottom: 15,
        borderRadius: 6
    };

    return (
        <div style={{ padding: '0 10px' }}>

            {/* Invoices Payable */}
            <div style={{ width: '50%', minWidth: 480 }}>
                <ReportWrapper
                    className='ag-theme-bootstrap'
                    style={{ height: paymentGridHeight, width: '100%' }}
                >
                    <AgGridReact
                        {...sharedGridProps}
                        columnDefs={paymentColumns}
                        rowData={paymentRowData}
                        pinnedBottomRowData={paymentTotals}
                        onGridReady={onGridReady}
                    />
                </ReportWrapper>
            </div>

            {/* CCB Accounts Receivable */}
            { isNkr && (<>
                <div style={{ width: '50%', minWidth: 480 }}>
                    <ReportWrapper
                        className='ag-theme-bootstrap'
                        style={{ height: receivableGridHeight, width: '100%' }}
                    >
                        <AgGridReact
                            {...sharedGridProps}
                            columnDefs={receivableColumns}
                            rowData={receivableRowData}
                            pinnedBottomRowData={receivableTotals}
                            onGridReady={onReceivableGridReady}
                        />
                    </ReportWrapper>
                </div>
            </>)}
        </div>
    );
};

export default Grid;